import React, { memo, useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { Row, Col, Spin, theme, List } from 'antd';
import VirtualList from 'rc-virtual-list';
import MessageBubble from './MessageBubble/MessageBubble';
import { useTheme } from '../../../../Shared/GlobalContext/ThemeContext';

const MessageList = memo(({
    messages,
    loading,
    messageRefs,
    handleReplyClick,
    onReplyAction,
    scrollApiRef,
    setSelectedMessage,
    setDrawerVisible,
    invertMessageSides = false,
    screens,
    onScroll,
    bottomInset = 0,
    hasMore = false,
    conversationId,
    TypingIndicator,
    CountdownIndicator,
    isFetchingMoreTop = false,
    isFetchingMoreBottom = false,
    highlightedMessageId
}) => {
    const { isDarkMode } = useTheme();
    const { token } = theme.useToken();

    const loadingSpinnerColor = isDarkMode ? token.colorTextLightSolid : token.colorText;

    // Order items: reverse real messages, then append temporary items at the end (bottom of visual list)
    const orderedMessages = useMemo(() => {
        const temporaryItems = (messages || []).filter(item => item?.isTemporary);
        const realItems = (messages || []).filter(item => !item?.isTemporary);
        // Messages are reversed for display (oldest first in DOM, newest last).
        // Temporary items (typing indicator) go LAST so they appear at the BOTTOM visually.
        return [...realItems.reverse(), ...temporaryItems];
    }, [messages]);

    const scrollContainerRef = useRef(null);
    const nearBottomRef = useRef(true);
    const shouldScrollToBottomRef = useRef(true);

    // Expose an imperative API to parent to control scrolling
    useEffect(() => {
        if (!scrollApiRef || typeof scrollApiRef !== 'object') return;
        scrollApiRef.current = {
            scrollToBottom: () => {
                const el = scrollContainerRef.current;
                if (!el) return;
                requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
            }
        };
        return () => { if (scrollApiRef) scrollApiRef.current = null; };
    }, [scrollApiRef]);

    // Callback ref for tracking last message (not used for scrolling)
    const lastMessageRef = useCallback((node) => {
        // Just for tracking, actual scrolling done via effect
    }, []);

    // Initial scroll to bottom when messages first load
    const hasScrolledInitiallyRef = useRef(false);
    useEffect(() => {
        if (loading || hasScrolledInitiallyRef.current) return;
        if (orderedMessages.length === 0) return;

        // First time messages load
        shouldScrollToBottomRef.current = true;
        hasScrolledInitiallyRef.current = true;
    }, [loading, orderedMessages.length]);

    // Reset scroll flag when conversation changes
    useEffect(() => {
        shouldScrollToBottomRef.current = true;
        nearBottomRef.current = true;
        hasScrolledInitiallyRef.current = false; // Reset for new conversation
    }, [conversationId]);

    // Unified scroll-to-bottom logic
    const prevFetchingBottomRef = useRef(false);

    useEffect(() => {
        // Track previous fetch state to detect completion
        const justFinishedFetchingBottom = prevFetchingBottomRef.current && !isFetchingMoreBottom;
        prevFetchingBottomRef.current = isFetchingMoreBottom;

        // Don't scroll if we're in the middle of loading older messages
        if (isLoadingOlderMessagesRef.current) return;

        // If we just finished bottom fetching, trust that the parent component restored scroll position
        if (justFinishedFetchingBottom) return;

        const el = scrollContainerRef.current;
        if (!el) return;

        // Determine if we should scroll
        // 1. Explicit flag set (e.g. initial load or conversation switch)
        // 2. Auto-scroll: User was near bottom AND we are not loading top OR bottom history
        const shouldExplicitScroll = shouldScrollToBottomRef.current;
        const shouldAutoScroll = !isFetchingMoreTop && !isFetchingMoreBottom && nearBottomRef.current && orderedMessages.length > 0;

        if (shouldExplicitScroll || shouldAutoScroll) {
            // Use requestAnimationFrame to ensure DOM has updated with new messages
            requestAnimationFrame(() => {
                if (el) { // Check ref again in callback
                    el.scrollTop = el.scrollHeight;
                    shouldScrollToBottomRef.current = false;
                }
            });
        }
    }, [orderedMessages.length, conversationId, isFetchingMoreTop, isFetchingMoreBottom]);

    const pendingTopLoadRef = useRef(false);
    const [topLoading, setTopLoading] = useState(false);
    const anchorMessageIdRef = useRef(null);
    const anchorOffsetRef = useRef(0);
    const isLoadingOlderMessagesRef = useRef(false);

    const handleScrollInternal = (e) => {
        const el = e.currentTarget;
        const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
        // Consider within 60px from bottom as "near bottom"
        nearBottomRef.current = distanceFromBottom <= 60;

        // If scrolled to absolute top, prepare anchor for preserving position
        if (el.scrollTop === 0) {
            const items = el.querySelectorAll('.message-item');
            let anchorEl = null;
            for (let i = 0; i < items.length; i += 1) {
                const item = items[i];
                if (item.offsetTop >= el.scrollTop) {
                    anchorEl = item;
                    break;
                }
            }
            if (!anchorEl && items.length > 0) anchorEl = items[0];
            if (anchorEl) {
                anchorMessageIdRef.current = anchorEl.getAttribute('data-message-id');
                anchorOffsetRef.current = anchorEl.offsetTop - el.scrollTop;
                pendingTopLoadRef.current = true;
                isLoadingOlderMessagesRef.current = true; // Mark that we're loading older messages
                if (hasMore) {
                    setTopLoading(true);
                }
            }
        }

        if (typeof onScroll === 'function') {
            onScroll(e);
        }
    };

    // After messages update, if we loaded more at the top, restore the anchor position
    useEffect(() => {
        if (!pendingTopLoadRef.current) return;
        const el = scrollContainerRef.current;
        if (!el) return;
        const anchorId = anchorMessageIdRef.current;
        if (!anchorId) return;
        const anchorEl = el.querySelector(`.message-item[data-message-id="${anchorId}"]`);
        if (anchorEl) {
            requestAnimationFrame(() => {
                el.scrollTop = anchorEl.offsetTop - anchorOffsetRef.current;
                // Hide the top loader after restoring position
                setTopLoading(false);
                // Clear the loading flag AFTER position is restored
                isLoadingOlderMessagesRef.current = false;
            });
        } else {
            // If anchor not found, still clear the flag
            isLoadingOlderMessagesRef.current = false;
        }
        pendingTopLoadRef.current = false;
        anchorMessageIdRef.current = null;
        anchorOffsetRef.current = 0;
    }, [orderedMessages.length]);

    return (
        <div style={{ height: '100%', width: '100%', position: 'relative' }}>
            <div ref={scrollContainerRef} style={{ height: '100%', overflowX: 'hidden', overflowY: 'auto', padding: screens?.xs ? 8 : 16, paddingBottom: bottomInset }} onScroll={handleScrollInternal}>
                {topLoading && hasMore && (
                    <Row justify="center" style={{ padding: 0 }}>
                        <LoadingSpinner color={loadingSpinnerColor} />
                    </Row>
                )}
                <div style={{
                    minHeight: '100%',
                    display: 'grid',
                    gridTemplateRows: '1fr auto',
                    gridTemplateAreas: '"spacer" "content"'
                }}>
                    <div style={{ gridArea: 'spacer' }}></div>
                    <div style={{ gridArea: 'content' }}>
                        <List>
                            <VirtualList
                                data={orderedMessages}
                                style={{
                                    height: 'auto',
                                    // Ensure only the outer container scrolls
                                    overflow: 'visible',
                                    padding: '0',
                                }}
                                itemHeight={80}
                                itemKey="id"
                                onScroll={onScroll}
                            >
                                {(item, index) => {
                                    const isLastItem = index === orderedMessages.length - 1;

                                    // Handle temporary indicator items
                                    if (item.isTemporary) {
                                        return (
                                            <div
                                                key={item.id}
                                                ref={isLastItem ? lastMessageRef : null}
                                                className="temporary-item"
                                                style={{ padding: '8px 0' }}
                                            >
                                                {item.type === 'typing-indicator' && TypingIndicator && (
                                                    <TypingIndicator isDarkMode={item.isDarkMode} />
                                                )}
                                                {item.type === 'countdown-indicator' && CountdownIndicator && (
                                                    <CountdownIndicator
                                                        isDarkMode={item.isDarkMode}
                                                        seconds={item.seconds}
                                                    />
                                                )}
                                            </div>
                                        );
                                    }

                                    // Handle regular message items
                                    const isHighlighted = item.id === highlightedMessageId;

                                    return (
                                        <div
                                            key={item.id}
                                            ref={isLastItem ? lastMessageRef : null}
                                            className={`message-item ${isHighlighted ? 'message-highlight-animation' : ''}`}
                                            data-message-id={item.id}
                                            style={{ padding: '8px 0' }}
                                        >
                                            <MessageBubble
                                                message={item}
                                                onReplyClick={handleReplyClick}
                                                onReplyAction={onReplyAction}
                                                messageRefs={messageRefs}
                                                onMessageSelect={(msg) => {
                                                    setSelectedMessage(msg);
                                                    setDrawerVisible(true);
                                                }}
                                                invertMessageSides={invertMessageSides}
                                                screens={screens}
                                            />
                                        </div>
                                    );
                                }}
                            </VirtualList>
                        </List>
                    </div>
                </div>
            </div>
        </div>
    );
});

const LoadingSpinner = ({ color }) => (
    <Row justify="center" style={{ padding: 20, order: 2 }}>
        <Spin style={{ color }} />
    </Row>
);

export default MessageList;
